import { FetchAPI } from '../Main/fetchAPI';
import { CLIENT, CLIENT_SOAP_NOTES, CLIENT_USERS, SESSIONS, SOAP_NOTES } from '../constants';

import {
	ApprovePendingSoapNotesProperties,
	CreateSoapNoteProperties,
	GetDSUSoapNotesProperties,
	GetExcelReportProperties,
	GetPendingSoapNotesProperties,
	GetSoapNoteProperties,
	GetSoapNotesProperties,
	UnlockSoapNoteProperties,
	UpdateAssessmentProperties,
	UpdateBehaviorTreatmentModificationProperties,
	UpdateBehaviorTreatmentProperties,
	UpdateBillingCredentialedProperties,
	UpdateDirectSupervisionProperties,
	UpdateDiscretionarySessionProperties,
	UpdateMeetAndGreetProperties,
	UpdateParentTrainingProperties,
	UpdateTreatmentPlanningProperties,
} from './soapNoteProperties';

export const SoapNote = {
	getPendingNotes: (data: GetPendingSoapNotesProperties) => {
		const {
			page,
			pageSize,
			direction,
			orderBy,
			searchString,
			status,
			endDate,
			startDate,
			clientId,
			userId,
			includeApproved,
		} = data;
		const query = new URLSearchParams();

		if (page) query.set('page', page);
		if (pageSize) query.set('pageSize', pageSize);
		if (searchString) query.set('searchString', searchString);
		if (direction) query.set('direction', direction);
		if (orderBy) query.set('orderBy', orderBy);
		if (status) query.set('status', status);
		if (startDate) query.set('startDate', startDate);
		if (endDate) query.set('endDate', endDate);
		if (clientId) query.set('clientId', clientId);
		if (userId) query.set('userId', userId);
		if (typeof includeApproved === 'boolean') query.set('includeApproved', `${includeApproved}`);

		return FetchAPI.getJson({
			urlStr: `${SOAP_NOTES}/pending-approval?${query.toString()}`,
		});
	},
	approvePendingNotes: (data: ApprovePendingSoapNotesProperties) => {
		const {
			page,
			pageSize,
			direction,
			orderBy,
			searchString,
			status,
			endDate,
			startDate,
			clientId,
			userId,
			includeApproved = false,
			signature,
			reportIds,
		} = data;
		const query = new URLSearchParams();

		if (page) query.set('page', page);
		if (pageSize) query.set('pageSize', pageSize);
		if (searchString) query.set('searchString', searchString);
		if (direction) query.set('direction', direction);
		if (orderBy) query.set('orderBy', orderBy);
		if (status) query.set('status', status);
		if (startDate) query.set('startDate', startDate);
		if (endDate) query.set('endDate', endDate);
		if (clientId) query.set('clientId', clientId);
		if (userId) query.set('userId', userId);
		if (typeof includeApproved === 'boolean') query.set('includeApproved', `${includeApproved}`);

		return FetchAPI.postJson({
			urlStr: `${SOAP_NOTES}/pending-approval/approve?${query.toString()}`,
			data: { signature, reportIds },
		});
	},
	updateBillingCredentialed: (data: UpdateBillingCredentialedProperties) =>
		FetchAPI.putJson({
			urlStr: `${CLIENT_USERS}/${data.userId}/soap-notes/update-billing-credentialed`,
			data,
		}),
	getSoapNotesUser: (data: GetSoapNotesProperties) => {
		const {
			page,
			pageSize,
			direction,
			orderBy,
			searchString,
			status,
			endDate,
			startDate,
			clientId,
			userId,
			includeBtSessions,
			sessionType,
		} = data;
		const query = new URLSearchParams();

		query.set('page', page);
		query.set('pageSize', pageSize);
		if (searchString) query.set('searchString', searchString);
		if (direction) query.set('direction', direction);
		if (orderBy) query.set('orderBy', orderBy);
		if (status) query.set('status', status);
		if (endDate) query.set('endDate', endDate);
		if (startDate) query.set('startDate', startDate);
		if (clientId) query.set('clientId', clientId);
		if (userId) query.set('userId', userId);
		if (sessionType) query.set('sessionType', sessionType);
		if (typeof includeBtSessions === 'boolean') query.set('includeBtSessions', `${includeBtSessions}`);

		return FetchAPI.getJson({ urlStr: `${SOAP_NOTES}?${query.toString()}` });
	},
	getPendingDSUSoapNotes: (data: GetExcelReportProperties) => {
		const { clientId, userId, endDate, startDate } = data;
		const query = new URLSearchParams();

		if (userId) query.set('userId', userId);
		if (clientId) query.set('clientId', clientId);
		if (endDate) query.set('endDate', endDate);
		if (startDate) query.set('startDate', startDate);

		return FetchAPI.getJson({
			urlStr: `${SOAP_NOTES}/pending-dsu/xlsx?${query.toString()}`,
		});
	},
	getDSUNotes: (data: GetDSUSoapNotesProperties) => {
		const {
			page,
			pageSize,
			direction,
			orderBy,
			searchString,
			status,
			endDate,
			startDate,
			clientId,
			userId,
			supervisedStatus,
		} = data;
		const query = new URLSearchParams();
		let statusIds = '';
		let supervisedStatusIds = '';

		if (status?.length) {
			statusIds = status.join('&status=');
		}

		if (supervisedStatus?.length) {
			supervisedStatusIds = supervisedStatus.join('&supervisedReportStatus=');
		}
		query.set('page', page);
		query.set('pageSize', pageSize);
		if (searchString) query.set('searchString', searchString);
		if (direction) query.set('direction', direction);
		if (orderBy) query.set('orderBy', orderBy);
		if (endDate) query.set('endDate', endDate);
		if (startDate) query.set('startDate', startDate);
		if (clientId) query.set('clientId', clientId);
		if (userId) query.set('userId', userId);

		return FetchAPI.getJson({
			urlStr: `${SOAP_NOTES}/dsu?${query.toString()}${status?.length ? `&status=${statusIds}` : ''}${
				supervisedStatus?.length ? `&supervisedReportStatus=${supervisedStatusIds}` : ''
			}`,
		});
	},
	downloadDSUNotes: (data: GetDSUSoapNotesProperties) => {
		const {
			page,
			pageSize,
			direction,
			orderBy,
			searchString,
			status,
			endDate,
			startDate,
			clientId,
			userId,
			supervisedStatus,
		} = data;
		const query = new URLSearchParams();
		let statusIds = '';
		let supervisedStatusIds = '';

		if (status?.length) {
			statusIds = status.join('&status=');
		}

		if (supervisedStatus?.length) {
			supervisedStatusIds = supervisedStatus.join('&supervisedReportStatus=');
		}
		query.set('page', page);
		query.set('pageSize', pageSize);
		if (searchString) query.set('searchString', searchString);
		if (direction) query.set('direction', direction);
		if (orderBy) query.set('orderBy', orderBy);
		if (endDate) query.set('endDate', endDate);
		if (startDate) query.set('startDate', startDate);
		if (clientId) query.set('clientId', clientId);
		if (userId) query.set('userId', userId);

		return FetchAPI.getJson({
			urlStr: `${SOAP_NOTES}/dsu/xlsx?${query.toString()}${status?.length ? `&status=${statusIds}` : ''}${
				supervisedStatus?.length ? `&supervisedReportStatus=${supervisedStatusIds}` : ''
			}`,
		});
	},
	getExpiredSessions: (data: GetExcelReportProperties) => {
		const { clientId, userId, endDate, startDate } = data;
		const query = new URLSearchParams();

		if (userId) query.set('userId', userId);
		if (clientId) query.set('clientId', clientId);
		if (endDate) query.set('endDate', endDate);
		if (startDate) query.set('startDate', startDate);

		return FetchAPI.getJson({
			urlStr: `${SESSIONS}/expired/xlsx?${query.toString()}`,
		});
	},
	getSoapNotesAdmin: (data: GetSoapNotesProperties) => {
		const {
			clientId,
			page,
			pageSize,
			direction,
			orderBy,
			searchString,
			status,
			startDate,
			endDate,
			userId,
			sessionType,
		} = data;
		const query = new URLSearchParams();

		query.set('page', page);
		query.set('pageSize', pageSize);
		if (searchString) query.set('searchString', searchString);
		if (direction) query.set('direction', direction);
		if (orderBy) query.set('orderBy', orderBy);
		if (status) query.set('status', status);
		if (startDate) query.set('startDate', startDate);
		if (endDate) query.set('endDate', endDate);
		if (clientId) query.set('clientId', clientId);
		if (userId) query.set('userId', userId);
		if (sessionType) query.set('sessionType', sessionType);

		return FetchAPI.getJson({
			urlStr: `${CLIENT}/admin/soap-notes?${query.toString()}`,
		});
	},
	getSoapNote: ({ clientId, reportId }: GetSoapNoteProperties) =>
		FetchAPI.getJson({
			urlStr: `${CLIENT_SOAP_NOTES(clientId)}/${reportId}`,
		}),
	getSoapNotePDF: ({ clientId, reportId }: GetSoapNoteProperties) =>
		FetchAPI.getJson({
			urlStr: `${CLIENT_SOAP_NOTES(clientId)}/${reportId}/pdf`,
		}),
	unlockSoapNote: ({ clientId, reportId, nextExpirationDate }: UnlockSoapNoteProperties) =>
		FetchAPI.postJson({
			urlStr: `${CLIENT_SOAP_NOTES(clientId)}/${reportId}/unlock`,
			data: { nextExpirationDate },
		}),
	createSoapNote: ({ clientId, sessionId }: CreateSoapNoteProperties) => {
		const query = new URLSearchParams();
		query.set('sessionId', sessionId);

		return FetchAPI.postJson({
			urlStr: `${CLIENT_SOAP_NOTES(clientId)}?${query.toString()}`,
		});
	},
	// Direct Supervision
	getDirectSupervisionOptions: (clientId: string, sessionId: string) => {
		const query = new URLSearchParams();
		query.set('sessionId', sessionId);

		return FetchAPI.getJson({
			urlStr: `${CLIENT_SOAP_NOTES(clientId)}/direct-supervision/options?${query.toString()}`,
		});
	},
	updateDirectSupervision: (data: UpdateDirectSupervisionProperties, signal?: AbortSignal) =>
		FetchAPI.putJson({
			urlStr: `${CLIENT_SOAP_NOTES(data.clientId)}/direct-supervision/${data.reportId}`,
			data,
			signal,
		}),
	// Behavior Treatment
	getBehaviorTreatmentOptions: (clientId: string, sessionId: string) => {
		const query = new URLSearchParams();
		query.set('sessionId', sessionId);

		return FetchAPI.getJson({
			urlStr: `${CLIENT_SOAP_NOTES(clientId)}/behavior-treatment/options?${query.toString()}`,
		});
	},
	updateBehaviorTreatment: (data: UpdateBehaviorTreatmentProperties, signal?: AbortSignal) =>
		FetchAPI.putJson({
			urlStr: `${CLIENT_SOAP_NOTES(data.clientId)}/behavior-treatment/${data.reportId}`,
			data,
			signal,
		}),
	// Behavior Treatment Modification
	getBehaviorTreatmentModificationOptions: (clientId: string, sessionId: string) => {
		const query = new URLSearchParams();
		query.set('sessionId', sessionId);

		return FetchAPI.getJson({
			urlStr: `${CLIENT_SOAP_NOTES(clientId)}/behavior-treatment-modification/options?${query.toString()}`,
		});
	},
	updateBehaviorTreatmentModification: (data: UpdateBehaviorTreatmentModificationProperties, signal?: AbortSignal) =>
		FetchAPI.putJson({
			urlStr: `${CLIENT_SOAP_NOTES(data.clientId)}/behavior-treatment-modification/${data.reportId}`,
			data,
			signal,
		}),
	// Assessment
	getAssessmentOptions: (clientId: string, sessionId: string) => {
		const query = new URLSearchParams();
		query.set('sessionId', sessionId);

		return FetchAPI.getJson({
			urlStr: `${CLIENT_SOAP_NOTES(clientId)}/assessment/options?${query.toString()}`,
		});
	},
	updateAssessment: (data: UpdateAssessmentProperties, signal?: AbortSignal) =>
		FetchAPI.putJson({
			urlStr: `${CLIENT_SOAP_NOTES(data.clientId)}/assessment/${data.reportId}`,
			data,
			signal,
		}),
	// Parent Training
	getParentTrainingOptions: (clientId: string, sessionId: string) => {
		const query = new URLSearchParams();
		query.set('sessionId', sessionId);

		return FetchAPI.getJson({
			urlStr: `${CLIENT_SOAP_NOTES(clientId)}/parent-training/options?${query.toString()}`,
		});
	},
	updateParentTraining: (data: UpdateParentTrainingProperties, signal?: AbortSignal) =>
		FetchAPI.putJson({
			urlStr: `${CLIENT_SOAP_NOTES(data.clientId)}/parent-training/${data.reportId}`,
			data,
			signal,
		}),
	// Treatment Planning
	getTreatmentPlanningOptions: (clientId: string, sessionId: string) => {
		const query = new URLSearchParams();
		query.set('sessionId', sessionId);

		return FetchAPI.getJson({
			urlStr: `${CLIENT_SOAP_NOTES(clientId)}/treatment-planning/options?${query.toString()}`,
		});
	},
	updateTreatmentPlanning: (data: UpdateTreatmentPlanningProperties, signal?: AbortSignal) =>
		FetchAPI.putJson({
			urlStr: `${CLIENT_SOAP_NOTES(data.clientId)}/treatment-planning/${data.reportId}`,
			data,
			signal,
		}),
	// Meet And Greet
	getMeetAndGreetOptions: (clientId: string, sessionId: string) => {
		const query = new URLSearchParams();
		query.set('sessionId', sessionId);

		return FetchAPI.getJson({
			urlStr: `${CLIENT_SOAP_NOTES(clientId)}/meetandgreet/options?${query.toString()}`,
		});
	},
	updateMeetAndGreet: (data: UpdateMeetAndGreetProperties, signal?: AbortSignal) =>
		FetchAPI.putJson({
			urlStr: `${CLIENT_SOAP_NOTES(data.clientId)}/meetandgreet/${data.reportId}`,
			data,
			signal,
		}),
	// Discretionary Session
	updateDiscretionarySession: (data: UpdateDiscretionarySessionProperties, signal?: AbortSignal) =>
		FetchAPI.putJson({
			urlStr: `${CLIENT_SOAP_NOTES(data.clientId)}/discretionary-session/${data.reportId}`,
			data,
			signal,
		}),
};
